/**
 * echoAudioProfiles.js
 * ─────────────────────────────────────────────────────────────
 * Voice catalogue adapted from the provided replacement file.
 *
 * We keep the richer profile model from the new file, and also
 * preserve the legacy exports that ReaderLab already depends on
 * so existing UI state, saved books, and selectors keep working.
 */

export const ECHO_VOICES = {
  "openai-uk-female": {
    id: "openai-uk-female",
    openaiVoice: "nova",
    label: "English · Warm Female",
    shortLabel: "Warm Female",
    language: "en",
    region: "UK",
    browserFallbackLang: "en-GB",
    gender: "female",
    quality: "recommended",
    narratorStyle:
      "You are narrating in a warm, clear, slightly British-inflected voice. Pace yourself gently — this is an audiobook, not a podcast."
  },
  "openai-uk-male": {
    id: "openai-uk-male",
    openaiVoice: "onyx",
    label: "English · Deep Male",
    shortLabel: "Deep Male",
    language: "en",
    region: "UK",
    browserFallbackLang: "en-GB",
    gender: "male",
    quality: "recommended",
    narratorStyle:
      "You are narrating in a deep, measured, authoritative voice. This is literary fiction — give the prose room to breathe."
  },
  "openai-us-english": {
    id: "openai-us-english",
    openaiVoice: "shimmer",
    label: "English · Bright American",
    shortLabel: "Bright American",
    language: "en",
    region: "US",
    browserFallbackLang: "en-US",
    gender: "female",
    quality: "standard",
    narratorStyle:
      "You are narrating in a clear, bright, American English voice. Keep a steady pace and enunciate fully."
  },
  "openai-narrative-male": {
    id: "openai-narrative-male",
    openaiVoice: "ash",
    label: "English · Narrative Male",
    shortLabel: "Narrative Male",
    language: "en",
    region: "US",
    browserFallbackLang: "en-US",
    gender: "male",
    quality: "enhanced",
    narratorStyle:
      "You are narrating in a warm, conversational, slightly intimate male voice. Suitable for memoirs and personal essays."
  },
  "openai-french-france": {
    id: "openai-french-france",
    openaiVoice: "sage",
    label: "Français · France",
    shortLabel: "France",
    language: "fr",
    region: "FR",
    browserFallbackLang: "fr-FR",
    gender: "female",
    quality: "recommended",
    narratorStyle:
      "Tu es narratrice d'un livre en français de France. Adopte une diction claire, soignée, sans accent régional marqué."
  },
  "openai-french-canada": {
    id: "openai-french-canada",
    openaiVoice: "shimmer",
    label: "Français · Canada",
    shortLabel: "Canada",
    language: "fr",
    region: "CA",
    browserFallbackLang: "fr-CA",
    gender: "female",
    quality: "standard",
    narratorStyle:
      "Tu es narratrice d'un livre en français canadien. Garde une diction naturelle, ni trop parisienne ni trop régionale."
  },
  "openai-taiwan-mandarin": {
    id: "openai-taiwan-mandarin",
    openaiVoice: "coral",
    label: "中文 · 台灣國語",
    shortLabel: "台灣國語",
    language: "zh",
    region: "TW",
    browserFallbackLang: "zh-TW",
    gender: "female",
    quality: "recommended",
    narratorStyle:
      "你是一位有聲書的女性朗讀者，使用台灣標準國語。語速平穩，咬字清晰，情感自然不誇張。"
  },
  "openai-mainland-mandarin": {
    id: "openai-mainland-mandarin",
    openaiVoice: "coral",
    label: "中文 · 普通話",
    shortLabel: "普通話",
    language: "zh",
    region: "CN",
    browserFallbackLang: "zh-CN",
    gender: "female",
    quality: "recommended",
    narratorStyle:
      "你是一位有聲書的女性朗讀者，使用標準普通話，接近央視播音風格。語調自然，不刻意煽情。"
  },
  "openai-cantonese": {
    id: "openai-cantonese",
    openaiVoice: "nova",
    label: "中文 · 廣東話",
    shortLabel: "廣東話",
    language: "yue",
    region: "HK",
    browserFallbackLang: "zh-HK",
    gender: "female",
    quality: "standard",
    narratorStyle:
      "你是一位有聲書的朗讀者，以廣東話朗讀。請保持自然的廣東話語調和語速。"
  },
  "openai-bilingual-en-zh": {
    id: "openai-bilingual-en-zh",
    openaiVoice: "coral",
    label: "雙語 · EN + 中文",
    shortLabel: "雙語 EN/中",
    language: "mixed",
    region: null,
    browserFallbackLang: "zh-TW",
    gender: "female",
    quality: "enhanced",
    narratorStyle:
      "You are narrating a bilingual text that alternates between English and Mandarin Chinese. When reading English paragraphs, use a clear, unhurried pace with slight warmth. When reading Chinese paragraphs, switch naturally to Standard Mandarin (普通話) without changing your overall tone or pace. Do not announce the language switch."
  }
};

export const VOICE_GROUPS = [
  {
    lang: "en",
    label: "English",
    zhLabel: "English",
    frLabel: "Anglais",
    voices: [
      "openai-uk-female",
      "openai-uk-male",
      "openai-us-english",
      "openai-narrative-male"
    ],
    default: "openai-uk-female"
  },
  {
    lang: "fr",
    label: "Français",
    zhLabel: "Français",
    frLabel: "Français",
    voices: ["openai-french-france", "openai-french-canada"],
    default: "openai-french-france"
  },
  {
    lang: "zh",
    label: "中文",
    zhLabel: "中文",
    frLabel: "Chinois",
    voices: [
      "openai-taiwan-mandarin",
      "openai-mainland-mandarin",
      "openai-cantonese"
    ],
    default: "openai-taiwan-mandarin"
  },
  {
    lang: "mixed",
    label: "Bilingual",
    zhLabel: "雙語",
    frLabel: "Bilingue",
    voices: ["openai-bilingual-en-zh"],
    default: "openai-bilingual-en-zh"
  }
];

export function getVoiceProfile(id) {
  return ECHO_VOICES[id];
}

export function getOpenAIVoice(profileId) {
  return ECHO_VOICES[profileId]?.openaiVoice ?? "nova";
}

export function getNarratorStyle(profileId) {
  return ECHO_VOICES[profileId]?.narratorStyle ?? "";
}

export function getBrowserFallbackLang(profileId) {
  return ECHO_VOICES[profileId]?.browserFallbackLang ?? "en-GB";
}

export function pickBrowserVoice(profileId) {
  if (typeof speechSynthesis === "undefined") {
    return null;
  }

  const voices = speechSynthesis.getVoices();

  if (!voices.length) {
    return null;
  }

  const targetLang = getBrowserFallbackLang(profileId);
  const langCode = targetLang.split("-")[0];
  const exact = voices.find((voice) => voice.lang === targetLang);

  if (exact) {
    return exact;
  }

  const sameLanguage = voices.filter((voice) =>
    String(voice.lang || "").startsWith(langCode)
  );
  const premium = sameLanguage.find((voice) =>
    /premium|enhanced/i.test(String(voice.name || ""))
  );

  if (premium) {
    return premium;
  }

  if (sameLanguage.length) {
    return sameLanguage[0];
  }

  return voices.find((voice) => String(voice.lang || "").startsWith("en")) || voices[0];
}

export function getVoicesForLanguage(lang) {
  const group = VOICE_GROUPS.find((entry) => entry.lang === lang);

  if (!group) {
    return [];
  }

  return group.voices.map((id) => ECHO_VOICES[id]).filter(Boolean);
}

export function getDefaultVoiceForLanguage(lang) {
  const group = VOICE_GROUPS.find((entry) => entry.lang === lang);
  return group?.default ?? "openai-uk-female";
}

const LEGACY_LABELS = {
  "openai-uk-female": {
    en: "UK Female",
    fr: "R.-U. femme",
    zh: "英国女声"
  },
  "openai-uk-male": {
    en: "UK Male",
    fr: "R.-U. homme",
    zh: "英国男声"
  },
  "openai-us-english": {
    en: "US",
    fr: "US",
    zh: "美式英语"
  },
  "openai-narrative-male": {
    en: "Narrative Male",
    fr: "Narrateur",
    zh: "叙述男声"
  },
  "openai-french-france": {
    en: "France",
    fr: "France",
    zh: "法国法语"
  },
  "openai-french-canada": {
    en: "Canada",
    fr: "Canada",
    zh: "加拿大法语"
  },
  "openai-taiwan-mandarin": {
    en: "Taiwan",
    fr: "Taïwan",
    zh: "台湾国语"
  },
  "openai-mainland-mandarin": {
    en: "Mainland",
    fr: "Chine continentale",
    zh: "中国普通话"
  },
  "openai-cantonese": {
    en: "Cantonese",
    fr: "Cantonais",
    zh: "粤语"
  },
  "openai-bilingual-en-zh": {
    en: "Bilingual",
    fr: "Bilingue",
    zh: "双语"
  }
};

function buildLegacyTargetsFromIds(ids) {
  return ids.map((id) => ({
    key: id,
    labels: LEGACY_LABELS[id] || {
      en: ECHO_VOICES[id]?.shortLabel || id,
      fr: ECHO_VOICES[id]?.shortLabel || id,
      zh: ECHO_VOICES[id]?.shortLabel || id
    }
  }));
}

export const ENGLISH_VOICE_TARGETS = buildLegacyTargetsFromIds(
  VOICE_GROUPS.find((group) => group.lang === "en")?.voices || []
);

export const FRENCH_VOICE_TARGETS = buildLegacyTargetsFromIds(
  VOICE_GROUPS.find((group) => group.lang === "fr")?.voices || []
);

export const CHINESE_VOICE_TARGETS = buildLegacyTargetsFromIds(
  VOICE_GROUPS.find((group) => group.lang === "zh")?.voices || []
);

export function buildVoiceOptions(targets, lang) {
  return targets.map((target) => ({
    voiceURI: target.key,
    optionLabel: target.labels?.[lang] || target.labels?.en || target.key
  }));
}

function isChineseLanguage(language) {
  const normalized = String(language || "").toLowerCase();
  return (
    normalized.startsWith("zh") ||
    normalized.startsWith("cmn") ||
    normalized.startsWith("yue")
  );
}

function isFrenchLanguage(language) {
  return String(language || "").toLowerCase().startsWith("fr");
}

function isMixedLanguage(language) {
  const normalized = String(language || "").toLowerCase();
  return normalized === "mixed" || normalized.includes("en-zh");
}

export function getPreferredVoiceForLanguage(language, availableVoices) {
  if (isMixedLanguage(language)) {
    return (
      availableVoices.mixed?.find(
        (voice) => voice.voiceURI === availableVoices.selectedMixedVoiceURI
      ) ||
      availableVoices.mixed?.[0] ||
      null
    );
  }

  if (isChineseLanguage(language)) {
    return (
      availableVoices.chinese.find(
        (voice) => voice.voiceURI === availableVoices.selectedChineseVoiceURI
      ) ||
      availableVoices.chinese[0] ||
      null
    );
  }

  if (isFrenchLanguage(language)) {
    return (
      availableVoices.french.find(
        (voice) => voice.voiceURI === availableVoices.selectedFrenchVoiceURI
      ) ||
      availableVoices.french[0] ||
      null
    );
  }

  return (
    availableVoices.english.find(
      (voice) => voice.voiceURI === availableVoices.selectedEnglishVoiceURI
    ) ||
    availableVoices.english[0] ||
    null
  );
}

export default ECHO_VOICES;
